/**
 * Referral Tracking Utilities
 */

import type { ReferralParams, ReferralSource, ReferralData } from '../types';
import { getCookie, setCookie } from './cookies';

const REFERRAL_COOKIE_NAME = 'engage_referral';

/**
 * Parse referral parameters from URL query string and hash fragment
 * @returns Object containing only non-null referral parameters
 */
export function parseReferralParams(): ReferralParams {
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.substring(1));

  // Comprehensive referral parameter mapping
  const referralParams: ReferralParams = {
    // Custom referral parameters
    ref: urlParams.get('ref') || hashParams.get('ref') || undefined,
    referral: urlParams.get('referral') || hashParams.get('referral') || undefined,
    referrer: urlParams.get('referrer') || hashParams.get('referrer') || undefined,

    // Standard UTM parameters
    utm_source: urlParams.get('utm_source') || undefined,
    utm_medium: urlParams.get('utm_medium') || undefined,
    utm_campaign: urlParams.get('utm_campaign') || undefined,
    utm_term: urlParams.get('utm_term') || undefined,
    utm_content: urlParams.get('utm_content') || undefined,

    // Platform-specific tracking parameters
    fbclid: urlParams.get('fbclid') || undefined,
    gclid: urlParams.get('gclid') || undefined,
    msclkid: urlParams.get('msclkid') || undefined,

    // Additional tracking parameters
    source: urlParams.get('source') || undefined,
    medium: urlParams.get('medium') || undefined,
    campaign: urlParams.get('campaign') || undefined,
    affiliate: urlParams.get('affiliate') || undefined,
    partner: urlParams.get('partner') || undefined,
  };

  // Return only parameters that have values
  return Object.fromEntries(
    Object.entries(referralParams).filter(
      ([, value]) => value !== null && value !== undefined
    )
  ) as ReferralParams;
}

/**
 * Detect referral source from document.referrer
 * @returns Referral source information or null if no referrer
 */
export function detectReferralSource(): ReferralSource | null {
  const referrer = document.referrer;
  if (!referrer) return null;

  try {
    const referrerUrl = new URL(referrer);
    const referrerDomain = referrerUrl.hostname.toLowerCase();

    // Known referral sources mapping for standardized analytics
    const knownSources: Record<string, string> = {
      // Search engines
      'google.com': 'google',
      'google.co.uk': 'google',
      'google.ca': 'google',
      'bing.com': 'bing',
      'yahoo.com': 'yahoo',
      'duckduckgo.com': 'duckduckgo',
      'yandex.com': 'yandex',
      'baidu.com': 'baidu',

      // Social media platforms
      'facebook.com': 'facebook',
      'twitter.com': 'twitter',
      'x.com': 'twitter',
      'linkedin.com': 'linkedin',
      'instagram.com': 'instagram',
      'tiktok.com': 'tiktok',
      'reddit.com': 'reddit',
      'pinterest.com': 'pinterest',

      // Other platforms
      'youtube.com': 'youtube',
      'github.com': 'github',
      'stackoverflow.com': 'stackoverflow',
      'medium.com': 'medium',
    };

    // Direct domain match
    if (knownSources[referrerDomain]) {
      return {
        source: knownSources[referrerDomain],
        domain: referrerDomain,
        url: referrer,
      };
    }

    // Partial domain match (for subdomains)
    for (const [domain, source] of Object.entries(knownSources)) {
      if (referrerDomain.includes(domain)) {
        return {
          source: source,
          domain: referrerDomain,
          url: referrer,
        };
      }
    }

    // Unknown external source
    return {
      source: 'external',
      domain: referrerDomain,
      url: referrer,
    };
  } catch (error) {
    // Invalid URL or other parsing error
    return {
      source: 'unknown',
      domain: 'unknown',
      url: referrer,
    };
  }
}

/**
 * Get or create referral data from URL parameters, existing cookies, or document.referrer
 * @param referralExpiryDays - Number of days to store referral data
 * @returns Complete referral data object or null if no referral detected
 */
export function getReferralData(referralExpiryDays = 30): ReferralData | null {
  const existingReferral = getCookie(REFERRAL_COOKIE_NAME);
  const urlParams = parseReferralParams();
  const referralSource = detectReferralSource();

  // Priority 1: New URL parameters detected (new referral campaign)
  if (Object.keys(urlParams).length > 0) {
    const referralData: ReferralData = {
      timestamp: new Date().toISOString(),
      urlParams: urlParams,
      source: referralSource,
      landingPage: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Store new referral data in cookie
    const cookieValue = encodeURIComponent(JSON.stringify(referralData));
    setCookie(REFERRAL_COOKIE_NAME, cookieValue, referralExpiryDays);

    return referralData;
  }

  // Priority 2: Existing referral data from previous visit
  if (existingReferral) {
    try {
      return JSON.parse(decodeURIComponent(existingReferral)) as ReferralData;
    } catch (error) {
      // Invalid cookie data, continue to check referrer
      console.warn('[EngageTrack] Invalid referral cookie data:', error);
    }
  }

  // Priority 3: Create referral data from document.referrer if available
  if (referralSource) {
    const referralData: ReferralData = {
      timestamp: new Date().toISOString(),
      urlParams: {},
      source: referralSource,
      landingPage: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Store referrer-based referral data
    const cookieValue = encodeURIComponent(JSON.stringify(referralData));
    setCookie(REFERRAL_COOKIE_NAME, cookieValue, referralExpiryDays);

    return referralData;
  }

  // No referral detected
  return null;
}
